import { createSlice } from '@reduxjs/toolkit';
import type { AsyncStatus } from '@/shared/types/asyncState';
import type { NotificacaoGlobal, NotificacaoGlobalDetalhe } from '../../types/operacoes';
import type { NotificacoesGlobaisStats } from './services';
import {
  fetchNotificacoesGlobais,
  fetchNotificacoesGlobaisStats,
  fetchNotificacaoGlobalById,
} from './actions';

interface NotificacoesGlobaisState {
  items: NotificacaoGlobal[];
  current: NotificacaoGlobalDetalhe | null;
  currentLoading: boolean;
  currentError: string | null;
  currentRequestId: string | null;
  stats: NotificacoesGlobaisStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  loading: boolean;
  statsLoading: boolean;
  error: string | null;
  status: AsyncStatus;
}

const initialState: NotificacoesGlobaisState = {
  items: [],
  current: null,
  currentLoading: false,
  currentError: null,
  currentRequestId: null,
  stats: { total: 0, enviadas: 0, falhas: 0, visualizadas: 0 },
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  loading: false,
  statsLoading: false,
  error: null,
  status: 'idle',
};

const notificacoesGlobaisSlice = createSlice({
  name: 'notificacoesGlobais',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.status = 'idle';
    },
    resetNotificacoesGlobais: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificacoesGlobais.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchNotificacoesGlobais.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
        state.status = 'success';
      })
      .addCase(fetchNotificacoesGlobais.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro desconhecido';
        state.status = 'error';
      })

      .addCase(fetchNotificacoesGlobaisStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchNotificacoesGlobaisStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchNotificacoesGlobaisStats.rejected, (state) => {
        state.statsLoading = false;
      })

      .addCase(fetchNotificacaoGlobalById.pending, (state, action) => {
        state.currentRequestId = action.meta.requestId;
        state.currentLoading = true;
        state.currentError = null;
        state.current = null;
      })
      .addCase(fetchNotificacaoGlobalById.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchNotificacaoGlobalById.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.currentLoading = false;
        state.currentError = action.payload ?? 'Erro ao carregar notificação';
      });
  },
});

export const { clearError, resetNotificacoesGlobais } = notificacoesGlobaisSlice.actions;
export const notificacoesGlobaisReducer = notificacoesGlobaisSlice.reducer;
